'use client';
import { PointerEvent as ReactPointerEvent, ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import { Category, type ID } from '@nutralab/api';
import { Button } from '@nutralab/ui';

import { classNames } from '@m1work/shared-utils';

import styles from './CategoryFilter.module.scss';

const NUDGE_STORAGE_KEY = 'categoryNudgeShown';
const NUDGE_DELAY = 300;
const DRAG_THRESHOLD = 5;
const WHEEL_LINE_HEIGHT = 16;

function isNudgeShown(): boolean {
    try {
        return sessionStorage.getItem(NUDGE_STORAGE_KEY) === '1';
    } catch {
        return false;
    }
}

function markNudgeShown(): void {
    try {
        sessionStorage.setItem(NUDGE_STORAGE_KEY, '1');
    } catch {
        return;
    }
}

interface DragState {
    pointerId: number;
    startX: number;
    startScrollLeft: number;
    active: boolean;
}

interface CategoryFilterProps {
    categories: Category[];
    selectedId: ID | null;
    onSelect: (id: ID | null) => void;
    allLabel: string;
    ariaLabel?: string;
    className?: string;
}

/** Chips to filter the products by category, scrollable (drag, wheel) when they overflow. */
export function CategoryFilter({
    categories,
    selectedId,
    onSelect,
    allLabel,
    ariaLabel,
    className,
}: CategoryFilterProps): ReactElement {
    const scrollerRef = useRef<HTMLDivElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const thumbRef = useRef<HTMLDivElement>(null);
    const thumbDragRef = useRef<DragState | null>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isThumbActive, setIsThumbActive] = useState(false);
    const [nudging, setNudging] = useState(false);

    const updateScrollState = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) {
            return;
        }
        const { scrollLeft, clientWidth, scrollWidth } = el;
        setCanScrollLeft(scrollLeft > 1);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
        setHasOverflow(scrollWidth > clientWidth + 1);

        const thumb = thumbRef.current;
        if (thumb && scrollWidth > 0 && clientWidth > 0) {
            thumb.style.width = `${(clientWidth / scrollWidth) * 100}%`;
            thumb.style.transform = `translateX(${(scrollLeft / clientWidth) * 100}%)`;
        }
    }, []);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) {
            return;
        }

        let frame = 0;
        const handleScroll = (): void => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(updateScrollState);
        };

        updateScrollState();
        el.addEventListener('scroll', handleScroll, { passive: true });
        const resizeObserver = new ResizeObserver(updateScrollState);
        resizeObserver.observe(el);

        return () => {
            cancelAnimationFrame(frame);
            el.removeEventListener('scroll', handleScroll);
            resizeObserver.disconnect();
        };
    }, [updateScrollState, categories]);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) {
            return;
        }

        const handleWheel = (e: WheelEvent): void => {
            if (Math.abs(e.deltaY) <= Math.abs(e.deltaX) || el.scrollWidth <= el.clientWidth) {
                return;
            }
            const unit = e.deltaMode === 1 ? WHEEL_LINE_HEIGHT : e.deltaMode === 2 ? el.clientWidth : 1;
            const delta = e.deltaY * unit;
            const maxScrollLeft = el.scrollWidth - el.clientWidth;
            if ((delta < 0 && el.scrollLeft <= 0) || (delta > 0 && el.scrollLeft >= maxScrollLeft - 1)) {
                return;
            }
            e.preventDefault();
            el.scrollLeft += delta;
        };

        let drag: DragState | null = null;

        const suppressClick = (e: MouseEvent): void => {
            e.preventDefault();
            e.stopPropagation();
        };

        const handlePointerDown = (e: PointerEvent): void => {
            if (e.pointerType !== 'mouse' || e.button !== 0 || el.scrollWidth <= el.clientWidth) {
                return;
            }
            drag = { pointerId: e.pointerId, startX: e.clientX, startScrollLeft: el.scrollLeft, active: false };
        };

        const handlePointerMove = (e: PointerEvent): void => {
            if (!drag || e.pointerId !== drag.pointerId) {
                return;
            }
            const dx = e.clientX - drag.startX;
            if (!drag.active) {
                if (Math.abs(dx) < DRAG_THRESHOLD) {
                    return;
                }
                drag.active = true;
                el.setPointerCapture(e.pointerId);
                setIsDragging(true);
            }
            el.scrollLeft = drag.startScrollLeft - dx;
        };

        const handlePointerUp = (e: PointerEvent): void => {
            if (!drag || e.pointerId !== drag.pointerId) {
                return;
            }
            if (drag.active) {
                el.addEventListener('click', suppressClick, { capture: true, once: true });
                setTimeout(() => el.removeEventListener('click', suppressClick, { capture: true }), 0);
                setIsDragging(false);
            }
            drag = null;
        };

        el.addEventListener('wheel', handleWheel, { passive: false });
        el.addEventListener('pointerdown', handlePointerDown);
        el.addEventListener('pointermove', handlePointerMove);
        el.addEventListener('pointerup', handlePointerUp);
        el.addEventListener('pointercancel', handlePointerUp);

        return () => {
            el.removeEventListener('wheel', handleWheel);
            el.removeEventListener('pointerdown', handlePointerDown);
            el.removeEventListener('pointermove', handlePointerMove);
            el.removeEventListener('pointerup', handlePointerUp);
            el.removeEventListener('pointercancel', handlePointerUp);
            el.removeEventListener('click', suppressClick, { capture: true });
        };
    }, []);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el || isNudgeShown() || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        let timeout: ReturnType<typeof setTimeout> | undefined;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry?.isIntersecting) {
                    return;
                }
                observer.disconnect();
                if (el.scrollWidth <= el.clientWidth || el.scrollLeft > 0) {
                    return;
                }
                markNudgeShown();
                timeout = setTimeout(() => setNudging(true), NUDGE_DELAY);
            },
            { threshold: 0.6 },
        );

        const cancel = (): void => {
            clearTimeout(timeout);
            observer.disconnect();
            setNudging(false);
        };

        observer.observe(el);
        el.addEventListener('pointerdown', cancel, { once: true });
        el.addEventListener('touchstart', cancel, { once: true, passive: true });

        return () => {
            cancel();
            el.removeEventListener('pointerdown', cancel);
            el.removeEventListener('touchstart', cancel);
        };
    }, []);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el || el.scrollWidth <= el.clientWidth) {
            return;
        }
        const button = el.querySelector<HTMLElement>(`[data-id="${selectedId ?? 'all'}"]`);
        if (!button) {
            return;
        }
        const left = button.offsetLeft - (el.clientWidth - button.offsetWidth) / 2;
        el.scrollTo({ left, behavior: 'smooth' });
    }, [selectedId]);

    const handleTrackPointerDown = (e: ReactPointerEvent<HTMLDivElement>): void => {
        const el = scrollerRef.current;
        if (!el || e.target !== e.currentTarget) {
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = (e.clientX - rect.left) / rect.width;
        el.scrollTo({ left: ratio * el.scrollWidth - el.clientWidth / 2, behavior: 'smooth' });
    };

    const handleThumbPointerDown = (e: ReactPointerEvent<HTMLDivElement>): void => {
        const el = scrollerRef.current;
        if (!el || e.button !== 0) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        thumbDragRef.current = {
            pointerId: e.pointerId,
            startX: e.clientX,
            startScrollLeft: el.scrollLeft,
            active: true,
        };
        setIsThumbActive(true);
    };

    const handleThumbPointerMove = (e: ReactPointerEvent<HTMLDivElement>): void => {
        const drag = thumbDragRef.current;
        const el = scrollerRef.current;
        const track = trackRef.current;
        if (!drag || !el || !track || e.pointerId !== drag.pointerId || track.clientWidth === 0) {
            return;
        }
        el.scrollLeft = drag.startScrollLeft + ((e.clientX - drag.startX) * el.scrollWidth) / track.clientWidth;
    };

    const handleThumbPointerUp = (): void => {
        thumbDragRef.current = null;
        setIsThumbActive(false);
    };

    return (
        <div className={classNames(styles.wrapper, className)}>
            <div
                className={classNames(
                    styles.viewport,
                    canScrollLeft && styles['fade-left'],
                    canScrollRight && styles['fade-right'],
                )}
            >
                <div
                    ref={scrollerRef}
                    className={classNames(
                        styles.scroller,
                        hasOverflow && styles['scroller-overflow'],
                        isDragging && styles.dragging,
                        nudging && styles.nudge,
                    )}
                    role="group"
                    aria-label={ariaLabel}
                    onAnimationEnd={() => setNudging(false)}
                >
                    <Button
                        data-id="all"
                        className={styles.chip}
                        variant="outlined"
                        selected={selectedId === null}
                        aria-pressed={selectedId === null}
                        onClick={() => onSelect(null)}
                    >
                        {allLabel}
                    </Button>
                    {categories.map((c) => (
                        <Button
                            key={c.id}
                            data-id={c.id}
                            className={styles.chip}
                            variant="outlined"
                            selected={selectedId === c.id}
                            aria-pressed={selectedId === c.id}
                            onClick={() => onSelect(c.id)}
                        >
                            {c.name}
                        </Button>
                    ))}
                </div>
            </div>

            <div
                ref={trackRef}
                className={classNames(styles.scrollbar, hasOverflow && styles['scrollbar-visible'], 'mt-1')}
                aria-hidden="true"
                onPointerDown={handleTrackPointerDown}
            >
                <div
                    ref={thumbRef}
                    className={classNames(styles.thumb, isThumbActive && styles['thumb-active'])}
                    onPointerDown={handleThumbPointerDown}
                    onPointerMove={handleThumbPointerMove}
                    onPointerUp={handleThumbPointerUp}
                    onPointerCancel={handleThumbPointerUp}
                />
            </div>
        </div>
    );
}
